/**
 * Error reporting (Sentry, EU data region), as in PrestoCRM. The SDK is its own chunk, loaded only
 * when VITE_SENTRY_DSN is set (deploys); its default integrations report uncaught errors and
 * unhandled rejections. No personal data is sent (`dataCollection`: no IP, user, cookies, headers,
 * bodies or query strings), so it needs no consent. `dataCollection` does not cover the page
 * address of an event or the URLs in breadcrumbs (PocketBase filters hold supplier names, /invite/
 * and the email links hold tokens), so scrubEvent and scrubBreadcrumb cut them down to the path,
 * ids replaced. React 19 hands render errors to window.reportError, which Sentry picks up.
 */
import type { Breadcrumb, ErrorEvent } from "@sentry/browser";
import { pagePath } from "@/lib/platform/analytics";
import { env } from "@/lib/platform/env";

export const NO_PERSONAL_DATA = {
  userInfo: false,
  cookies: false,
  httpHeaders: false,
  httpBodies: [],
  urlQueryParams: false,
  stackFrameVariables: false,
};

/** Call once at startup (main.tsx). A failed download only means no reports. */
export async function initErrorReporting(): Promise<void> {
  if (!env.sentryDsn) return;
  try {
    const Sentry = await import("@sentry/browser");
    Sentry.init({
      dsn: env.sentryDsn,
      environment: env.sentryEnvironment,
      dataCollection: NO_PERSONAL_DATA,
      beforeSend: scrubEvent,
      beforeBreadcrumb: scrubBreadcrumb,
    });
  } catch {
    // Offline or blocked: the app works the same without it.
  }
}

/** An address without query, hash, record ids or tokens. */
export function scrubUrl(value: string): string {
  try {
    const url = new URL(value, window.location.origin);
    return url.origin + pagePath(url.pathname);
  } catch {
    return "";
  }
}

export function scrubEvent(event: ErrorEvent): ErrorEvent {
  if (event.request?.url) event.request.url = scrubUrl(event.request.url);
  if (event.request) delete event.request.query_string;
  return event;
}

/** Console lines can print records; requests and navigations keep their path only. */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  if (breadcrumb.category === "console") return null;
  const data = breadcrumb.data;
  if (data) {
    for (const key of ["url", "from", "to"]) {
      if (typeof data[key] === "string") data[key] = scrubUrl(data[key]);
    }
  }
  return breadcrumb;
}
